import React, { useEffect, useState } from "react";
import * as Icon from "react-feather";
import { Button, Form, Input, InputNumber, Modal, Select, Switch, Tabs } from "antd";
import { useDispatch } from "react-redux";
import intranetService from "../../services/intranet-service";
import appLoaderAction from "../../redux/action/app-loader-action";
import notifyUtitls from "../../utils/notify-utils";
import ContentSelect from "../../components/input/content-select";
import { LINK_TYPE } from "../../constants/app-constants";
import { FORM_LANG, formText, langOfTab } from "../../constants/form-i18n";
import { INTRANET_ICONS, INTRANET_ICON_COLOR } from "../../constants/intranet-icons";
import { isUnsafeLink, withProtocol } from "../../utils/url-utils";

const { TabPane } = Tabs;
const { TextArea } = Input;

/**
 * NMU-478: add / edit one staff-intranet portal card.
 *
 * One dialog for all five sections — the card shape is identical across them,
 * and the section is fixed by whichever tab the editor is on rather than being
 * a field they can get wrong.
 *
 * The icon list is the set the portal actually renders (see
 * constants/intranet-icons); a free-text field would let an editor type a name
 * that silently renders nothing.
 */

/**
 * NMU-488: one row of the icon picker — the glyph beside its name, used both in
 * the open dropdown and as the closed field's value.
 *
 * antd v4 builds the selected value from the Option's children, so drawing the
 * icon here is what makes the closed field show it too; `optionLabelProp` would
 * be needed only if the two had to differ, and they shouldn't.
 */
const IconOptionLabel = ({ option }) => {
  const Glyph = option.icon;
  return (
    <span className="d-inline-flex align-items-center">
      <Glyph size={20} color={INTRANET_ICON_COLOR} className="me-2" />
      {option.label}
    </span>
  );
};

const AddEditIntranetCardDialog = ({ isShow, sectionKey, onCancel, onSaveComplete, editData }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [lang, setLang] = useState(FORM_LANG.TH);
  const t = formText(lang);

  useEffect(() => {
    if (!isShow) return;
    if (editData) {
      form.setFieldsValue({
        titleTH: editData.title && editData.title.titleTH,
        titleEN: editData.title && editData.title.titleEN,
        badgeTH: editData.badgeTH,
        badgeEN: editData.badgeEN,
        descriptionTH: editData.descriptionTH,
        descriptionEN: editData.descriptionEN,
        icon: editData.icon || undefined,
        linkType: editData.linkType || (editData.templateId ? LINK_TYPE.CONTENT : LINK_TYPE.URL),
        templateId: editData.templateId,
        url: editData.url,
        seqNo: editData.seqNo,
        enableNewTab: !!editData.enableNewTab,
        status: !!editData.status,
      });
    } else {
      form.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShow, editData]);

  const onSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (err) {
      notifyUtitls.warning(t("warningTitle"), t("incompleteForm"));
      return;
    }

    try {
      setLoading(true);
      dispatch(appLoaderAction.enable());

      const bean = {
        ...(editData ? JSON.parse(JSON.stringify(editData)) : {}),
        // The section comes from the open tab, never from user input.
        sectionKey,
        title: { titleTH: values.titleTH, titleEN: values.titleEN },
        badgeTH: values.badgeTH,
        badgeEN: values.badgeEN,
        descriptionTH: values.descriptionTH,
        descriptionEN: values.descriptionEN,
        icon: values.icon,
        linkType: values.linkType,
        // Clear the half that does not belong to the chosen type.
        templateId: values.linkType === LINK_TYPE.CONTENT ? values.templateId : null,
        // NMU-602: saved with its https:// even if the field was never left.
        url: values.linkType === LINK_TYPE.URL ? withProtocol(values.url) : null,
        seqNo: values.seqNo === undefined || values.seqNo === null ? null : Number(values.seqNo),
        enableNewTab: !!values.enableNewTab,
        status: !!values.status,
      };

      if (editData) {
        await intranetService.edit(editData.intranetCardId, bean);
      } else {
        await intranetService.add(bean);
      }

      notifyUtitls.success(t("successTitle"), t("saveSuccess"));
      onSaveComplete();
    } catch (err) {
      notifyUtitls.error(err);
    } finally {
      dispatch(appLoaderAction.disale());
      setLoading(false);
    }
  };

  return (
    <Modal
      title={editData ? "แก้ไขรายการ" : "เพิ่มรายการ"}
      centered
      width={720}
      visible={isShow}
      onCancel={onCancel}
      destroyOnClose
      footer={[
        <Button key="submit" type="primary" onClick={onSave} loading={loading}>
          <Icon.Save className="me-2" size={17} />
          {t("save")}
        </Button>,
        <Button key="back" className="btn-gray" onClick={onCancel}>
          {t("cancel")}
        </Button>,
      ]}
    >
      <Form
        layout="vertical"
        form={form}
        preserve={false}
        initialValues={{ status: true, enableNewTab: true }}
        validateMessages={{ required: t("required") }}
      >
        <Tabs defaultActiveKey="1" onChange={(key) => setLang(langOfTab(key))}>
          <TabPane tab={t("tabThai")} key="1" forceRender>
            <Form.Item label="ชื่อรายการ (ไทย)" name="titleTH" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item label="ป้ายกำกับ (ไทย)" name="badgeTH">
              <Input placeholder="เช่น สิทธิ์ของท่าน / ลาออนไลน์" />
            </Form.Item>
            <Form.Item label="คำอธิบาย (ไทย)" name="descriptionTH">
              <TextArea rows={3} />
            </Form.Item>
          </TabPane>
          <TabPane tab={t("tabEnglish")} key="2" forceRender>
            <Form.Item label="Title (EN)" name="titleEN">
              <Input />
            </Form.Item>
            <Form.Item label="Badge / Tag (EN)" name="badgeEN">
              <Input />
            </Form.Item>
            <Form.Item label="Description (EN)" name="descriptionEN">
              <TextArea rows={3} />
            </Form.Item>
          </TabPane>
        </Tabs>

        <Form.Item label="ไอคอน" name="icon">
          <Select allowClear placeholder="เลือกไอคอน">
            {INTRANET_ICONS.map((opt) => (
              <Select.Option key={opt.value} value={opt.value} label={opt.label}>
                <IconOptionLabel option={opt} />
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label={t("linkType")} name="linkType" rules={[{ required: true }]}>
          <Select placeholder={t("linkTypePlaceholder")}>
            <Select.Option value={LINK_TYPE.CONTENT}>{t("linkTypeContent")}</Select.Option>
            <Select.Option value={LINK_TYPE.URL}>{t("linkTypeUrl")}</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.linkType !== cur.linkType}>
          {({ getFieldValue }) =>
            getFieldValue("linkType") === LINK_TYPE.CONTENT ? (
              <Form.Item label={t("contentField")} name="templateId" rules={[{ required: true }]}>
                <ContentSelect placeholder={t("contentPlaceholder")} />
              </Form.Item>
            ) : getFieldValue("linkType") === LINK_TYPE.URL ? (
              /*
                NMU-602: `egp.nmu.ac.th` becomes https://egp.nmu.ac.th as the
                editor leaves the field, so the address they see is the one
                saved. Typed as-is it was a path on the site, and a 404.
              */
              <Form.Item
                label={t("link")}
                name="url"
                rules={[
                  { required: true, whitespace: true },
                  {
                    validator: (_, value) =>
                      isUnsafeLink(value)
                        ? Promise.reject(new Error("ลิงก์ต้องเป็นที่อยู่เว็บไซต์ เช่น https://egp.nmu.ac.th"))
                        : Promise.resolve(),
                  },
                ]}
              >
                <Input
                  placeholder="https://..."
                  onBlur={(e) => {
                    const formatted = withProtocol(e.target.value);
                    if (formatted !== e.target.value) form.setFieldsValue({ url: formatted });
                  }}
                />
              </Form.Item>
            ) : null
          }
        </Form.Item>

        <Form.Item label="ลำดับการแสดงผล" name="seqNo">
          <InputNumber min={1} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item label={t("openInNewTab")} name="enableNewTab" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item label={t("status")} name="status" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddEditIntranetCardDialog;
